import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, RefreshCcw, Mail, CheckCircle2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const TYPE_LABELS = {
  tutor_welcome: 'Tutor Welcome',
  tutor_application_admin_alert: 'New Application Alert',
  student_enquiry_admin_alert: 'Enquiry Admin Alert',
  student_enquiry_confirmation: 'Enquiry Confirmation',
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-AU', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const EmailLogsPanel = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'failed' | 'success'

  const fetchLogs = useCallback(async () => {
    try {
      setRefreshing(true);
      const res = await fetch(`${BACKEND_URL}/api/v4/admin/email-logs?limit=100`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to load email logs');
      const data = await res.json();
      setLogs(data.logs || []);
    } catch (error) {
      console.error('Email logs fetch error:', error);
      toast.error('Failed to load email logs');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const visible = logs.filter((l) => {
    if (statusFilter === 'failed') return l.status !== 'success';
    if (statusFilter === 'success') return l.status === 'success';
    return true;
  });

  const failedCount = logs.filter((l) => l.status !== 'success').length;

  const handleRetry = async (log) => {
    // Best-effort retry through admin resend endpoint (only for booking_*)
    const bookingId = log.booking_id;
    const role = (log.recipient_role || '').toLowerCase();
    if (!bookingId || !['tutor', 'student'].includes(role)) {
      toast.error('Retry unavailable — original message has no booking_id / role.');
      return;
    }
    try {
      const res = await fetch(`${BACKEND_URL}/api/classroom/admin/bookings/${bookingId}/resend-invite`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role }),
      });
      const j = await res.json();
      const r = j?.results?.[role];
      if (r?.status === 'sent') toast.success(`Resent · message_id ${r.email_id?.slice(0, 8) || ''}`);
      else if (r?.status === 'skipped') toast.info(r.error_message || 'Skipped');
      else toast.error(r?.error_message || 'Resend failed');
      fetchLogs();
    } catch (e) {
      toast.error('Retry failed: ' + (e.message || 'unknown'));
    }
  };

  return (
    <Card data-testid="v5-email-logs-panel">
      <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-3">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Email Log {failedCount > 0 && (
              <Badge className="bg-red-100 text-red-800 border border-red-200 ml-1">{failedCount} failed</Badge>
            )}
          </CardTitle>
          <p className="text-sm text-gray-500 mt-1">
            Last {logs.length} outgoing emails. Retry failed booking invites directly from this panel.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="h-9 px-3 rounded-md border border-gray-200 text-xs"
            data-testid="v5-email-logs-filter"
          >
            <option value="all">All ({logs.length})</option>
            <option value="failed">Failed ({failedCount})</option>
            <option value="success">Sent ({logs.length - failedCount})</option>
          </select>
          <Button
            size="sm"
            variant="outline"
            onClick={fetchLogs}
            disabled={refreshing}
            className="gap-2"
            data-testid="v5-email-logs-refresh"
          >
            {refreshing ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCcw className="h-4 w-4" />
            )}
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        ) : visible.length === 0 ? (
          <div className="text-center py-12 text-gray-500" data-testid="v5-email-logs-empty">
            No outgoing emails {statusFilter !== 'all' ? `in '${statusFilter}' state` : 'logged yet'}.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wide text-gray-500 border-b">
                <tr>
                  <th className="text-left py-2 pr-4">Status</th>
                  <th className="text-left py-2 pr-4">Type</th>
                  <th className="text-left py-2 pr-4">Recipient</th>
                  <th className="text-left py-2 pr-4">Subject</th>
                  <th className="text-left py-2 pr-4">Email ID / Error</th>
                  <th className="text-left py-2 pr-4">Sent</th>
                  <th className="text-left py-2 pr-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((log) => {
                  const isSuccess = log.status === 'success';
                  const canRetry = !isSuccess && log.booking_id && ['tutor', 'student'].includes((log.recipient_role || '').toLowerCase());
                  return (
                    <tr
                      key={log.id}
                      className="border-b last:border-b-0 hover:bg-gray-50"
                      data-testid={`v5-email-log-row-${log.id}`}
                    >
                      <td className="py-3 pr-4">
                        {isSuccess ? (
                          <Badge className="gap-1 bg-green-100 text-green-800 border border-green-200">
                            <CheckCircle2 className="h-3.5 w-3.5" />
                            Sent
                          </Badge>
                        ) : (
                          <Badge className="gap-1 bg-red-100 text-red-800 border border-red-200">
                            <AlertCircle className="h-3.5 w-3.5" />
                            Failed
                          </Badge>
                        )}
                      </td>
                      <td className="py-3 pr-4 text-gray-700">
                        {TYPE_LABELS[log.email_type] || log.email_type}
                      </td>
                      <td className="py-3 pr-4 font-medium text-gray-900 break-all">
                        {log.recipient}
                      </td>
                      <td className="py-3 pr-4 text-gray-700 max-w-xs truncate" title={log.subject}>
                        {log.subject}
                      </td>
                      <td className="py-3 pr-4 text-gray-500 font-mono text-xs break-all max-w-xs" title={log.error_message || log.email_id}>
                        {log.email_id || (
                          <span className="italic text-red-600">{log.error_message || log.error_code || '—'}</span>
                        )}
                      </td>
                      <td className="py-3 pr-4 text-gray-600 whitespace-nowrap">
                        {formatDate(log.sent_at)}
                      </td>
                      <td className="py-3 pr-4">
                        {canRetry && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleRetry(log)}
                            className="text-xs gap-1"
                            data-testid={`v5-email-log-retry-${log.id}`}
                          >
                            <RefreshCcw className="h-3 w-3" /> Retry
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default EmailLogsPanel;
